import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Wallet, Store, ChevronLeft, ChevronRight, IndianRupee, Users, AlertTriangle,
  Undo2, CircleCheck, Settings2, Receipt, SlidersHorizontal, Filter,
} from "lucide-react";
import { getPayrollSheet } from "../../api/payroll";
import { listBranches } from "../../api/branches";
import { listEmployees } from "../../api/employees";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Card, Button, Select, EmptyState, TableSkeleton, StatCard, Skeleton, Avatar } from "../../components/ui";
import { PayModal, AdjustModal, ReverseModal } from "./PayrollActionModals";
import { ActiveFilters } from "../../components/Filters";
import { unwrapList, errorMessage, currentMonth, shiftMonth, formatMonth, formatMinutes } from "../../lib/records";
import { formatMoney } from "../../lib/salary";
import { PAYROLL_STATUS_STYLES, PAYROLL_STATUS_LABELS } from "../../lib/enums";

// §11.4 — the month's payroll sheet: the screen you run payday from.
//
// A month nobody has touched has no stored record (`_id` is null) and is
// computed live from attendance + salary, so it can never be stale. The first
// adjustment or payment snapshots it; once fully PAID it freezes.
export default function PayrollList() {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";

  const [searchParams, setSearchParams] = useSearchParams();
  const [month, setMonth] = useState(currentMonth());
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  // §11.4 takes an `employee` filter, which is what lets an employee's own page
  // deep-link into this exact screen rather than a reduced copy of it.
  const [employeeId, setEmployeeId] = useState(() => searchParams.get("employee") || "");
  const [employees, setEmployees] = useState([]);
  // Narrows the month's rows client-side — see the memo below.
  const [payFilter, setPayFilter] = useState("");
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [payRow, setPayRow] = useState(null);
  const [adjustRow, setAdjustRow] = useState(null);
  const [reverseRow, setReverseRow] = useState(null);

  useEffect(() => {
    if (!isChain) return;
    listBranches({ limit: 100 })
      .then((res) => setBranches(unwrapList(res, 100).items))
      .catch(() => setBranches([]));
  }, [isChain]);

  useEffect(() => {
    listEmployees({ limit: 100, status: "ACTIVE", ...(isChain && branchId ? { partner: branchId } : {}) })
      .then((res) => setEmployees(unwrapList(res, 100).items))
      .catch(() => setEmployees([]));
  }, [branchId, isChain]);

  const fetchSheet = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await getPayrollSheet({
        month,
        ...(employeeId ? { employee: employeeId } : {}),
        ...(isChain && branchId ? { partner: branchId } : {}),
      });
      setSheet(res?.data ?? null);
    } catch (err) {
      setSheet(null);
      setLoadError(errorMessage(err, "The payroll sheet could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [month, employeeId, branchId, isChain]);

  useEffect(() => {
    fetchSheet();
  }, [fetchSheet]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (employeeId) next.set("employee", employeeId);
    else next.delete("employee");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const totals = sheet?.totals;
  const allItems = sheet?.items ?? [];

  // §11.4 has no status filter, and the sheet is one month of staff rather
  // than a paged list — so this narrows the rows already in hand. "Unpaid"
  // is the payday view: everyone still owed something.
  const items = useMemo(() => {
    if (!payFilter) return allItems;
    if (payFilter === "UNPAID") return allItems.filter((r) => (r.dueAmount ?? 0) > 0);
    if (payFilter === "NO_SALARY") return allItems.filter((r) => !r.salarySet);
    return allItems.filter((r) => r.status === payFilter);
  }, [allItems, payFilter]);

  const isCurrentMonth = month === currentMonth();
  // Days someone started but never ended count 0 minutes until corrected, so
  // they quietly suppress pay — worth chasing before payday.
  const openDayCount = items.reduce((sum, r) => sum + (r.attendance?.openDays ?? 0), 0);

  function afterAction() {
    setPayRow(null);
    setAdjustRow(null);
    setReverseRow(null);
    fetchSheet();
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8">
        <PageHeader
          title="Payroll"
          subtitle="Salary earned from attendance, adjusted, and paid — one month at a time."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Link to="/payroll/history">
                <Button
                  variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 text-sm px-3 py-1.5"
                >
                  <Receipt size={15} className="hidden sm:inline mr-1" /> Paid & due
                </Button>
              </Link>
              <Link to="/payroll/salaries">
                <Button
                  variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 text-sm px-3 py-1.5"
                >
                  <Settings2 size={15} className="hidden sm:inline mr-1" /> Salaries
                </Button>
              </Link>
              {isChain && (
                <div className="flex items-center gap-2">
                  <Store size={15} className="shrink-0 text-gray-400" />
                  <Select
                    value={branchId}
                    onChange={(e) => {
                      setBranchId(e.target.value);
                      setEmployeeId("");
                    }}
                    className="w-full sm:w-44 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                  >
                    <option value="">All branches</option>
                    {branches.map((b) => (
                      <option key={b._id} value={b._id}>
                        {b.name}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
              <Select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                className="w-full sm:w-44 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
              >
                <option value="">Everyone</option>
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.name}
                  </option>
                ))}
              </Select>
            </div>
          }
        />

        {/* Month picker. Future months are rejected by the API, so forward
            navigation stops at the current one. */}
        <Card className="mb-4 sm:mb-5 flex items-center justify-between gap-3 p-3 sm:p-4 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-lg shadow-emerald-100/40">
          <button
            onClick={() => setMonth(shiftMonth(month, -1))}
            className="rounded-md p-1.5 text-gray-500 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
            title="Previous month"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="text-center">
            <p className="text-sm sm:text-base font-semibold text-gray-800">{formatMonth(month)}</p>
            {sheet?.dayBasis && (
              <p className="text-xs text-gray-500">Priced on a {sheet.dayBasis}-day month</p>
            )}
          </div>
          <button
            onClick={() => setMonth(shiftMonth(month, 1))}
            disabled={isCurrentMonth}
            className="rounded-md p-1.5 text-gray-500 hover:bg-emerald-50 hover:text-emerald-700 transition-colors disabled:opacity-30 disabled:pointer-events-none"
            title="Next month"
          >
            <ChevronRight size={18} />
          </button>
        </Card>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="p-3 sm:p-5 bg-white/80 backdrop-blur-sm border border-emerald-100">
                <Skeleton className="h-3 sm:h-4 w-20 sm:w-24" />
                <Skeleton className="mt-2 sm:mt-3 h-6 sm:h-8 w-12 sm:w-16" />
              </Card>
            ))
          ) : (
            <>
              <StatCard label="Net payable" value={formatMoney(totals?.netPayable ?? 0)} icon={Wallet} tone="primary" />
              <StatCard label="Paid" value={formatMoney(totals?.paidAmount ?? 0)} icon={CircleCheck} tone="green" />
              <StatCard label="Still due" value={formatMoney(totals?.dueAmount ?? 0)} icon={IndianRupee} tone="orange" />
              <StatCard
                label="Settled"
                value={`${totals?.paidEmployees ?? 0}/${totals?.employees ?? 0}`}
                icon={Users}
                tone="blue"
              />
            </>
          )}
        </div>

        {openDayCount > 0 && !loading && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>
              <strong>{openDayCount} open {openDayCount === 1 ? "day" : "days"}</strong> — someone started
              a shift but never ended it. Those days count zero minutes until corrected, which suppresses
              pay. <Link to="/attendance" className="font-semibold underline">Fix them in attendance</Link> before
              you run payday.
            </span>
          </div>
        )}

        {loadError && !loading && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
            {loadError}
          </div>
        )}

        <Card className="mt-4 sm:mt-5 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50 overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-emerald-100/60 p-3 sm:p-4">
            <div className="flex flex-wrap items-center gap-1.5">
              <Filter size={14} className="shrink-0 text-gray-400" />
              {[
                { key: "", label: "Everyone" },
                { key: "UNPAID", label: "Still owed" },
                { key: "PARTIALLY_PAID", label: "Part paid" },
                { key: "PAID", label: "Settled" },
                { key: "NO_SALARY", label: "No salary" },
              ].map((f) => {
                const on = payFilter === f.key;
                const count =
                  f.key === ""
                    ? allItems.length
                    : f.key === "UNPAID"
                      ? allItems.filter((r) => (r.dueAmount ?? 0) > 0).length
                      : f.key === "NO_SALARY"
                        ? allItems.filter((r) => !r.salarySet).length
                        : allItems.filter((r) => r.status === f.key).length;
                return (
                  <button
                    key={f.key || "all"}
                    onClick={() => setPayFilter(f.key)}
                    className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      on
                        ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-sm"
                        : "bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700"
                    }`}
                  >
                    {f.label}
                    <span className={on ? "ml-1 text-white/70" : "ml-1 text-gray-400"}>{count}</span>
                  </button>
                );
              })}
            </div>

            <ActiveFilters
              resultCount={loading ? null : items.length}
              filters={[
                {
                  label: "Employee",
                  value: employees.find((e) => e._id === employeeId)?.name,
                  onClear: () => setEmployeeId(""),
                },
                {
                  label: "Branch",
                  value: branches.find((b) => b._id === branchId)?.name,
                  onClear: () => setBranchId(""),
                },
                {
                  label: "Showing",
                  value:
                    payFilter === "UNPAID"
                      ? "Still owed"
                      : payFilter === "NO_SALARY"
                        ? "No salary"
                        : payFilter
                          ? PAYROLL_STATUS_LABELS[payFilter]
                          : "",
                  onClear: () => setPayFilter(""),
                },
              ]}
              onClearAll={() => {
                setEmployeeId("");
                setBranchId("");
                setPayFilter("");
              }}
            />
          </div>

          {loading ? (
            <TableSkeleton rows={6} cols={6} />
          ) : items.length === 0 ? (
            <div className="p-8">
              <EmptyState
                icon={Wallet}
                title="Nothing to pay this month"
                description="Set salaries on your employees and record their attendance, and this sheet fills itself in."
                action={
                  <Link to="/payroll/salaries">
                    <Button className="bg-gradient-to-r from-emerald-500 to-green-600 text-white">
                      Set salaries
                    </Button>
                  </Link>
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] text-left">
                <thead>
                  <tr className="text-xs sm:text-label-sm uppercase tracking-wide text-gray-500 border-b border-emerald-100">
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Employee</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Salary</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Attendance</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Earned</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Net</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Due</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Status</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 text-right w-32">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-100/50">
                  {items.map((row) => {
                    const att = row.attendance ?? {};
                    const noSalary = !row.salarySet;
                    return (
                      <tr
                        key={row.employee?._id ?? row.employee}
                        className="text-xs sm:text-body-sm text-gray-800 hover:bg-emerald-50/50 transition-colors duration-200"
                      >
                        <td className="px-3 sm:px-4 py-2 sm:py-3">
                          <Link
                            to={`/employees/${row.employee?._id}`}
                            className="flex items-center gap-2 group/emp"
                          >
                            <Avatar name={row.employee?.name ?? "?"} size={28} />
                            <div className="min-w-0">
                              <p className="truncate text-xs font-medium text-gray-800 group-hover/emp:text-emerald-700 transition-colors">
                                {row.employee?.name ?? "Unknown"}
                              </p>
                              {row.employee?.designation && (
                                <p className="truncate text-xs text-gray-500">{row.employee.designation}</p>
                              )}
                            </div>
                          </Link>
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3">
                          {noSalary ? (
                            <Link to="/payroll/salaries" className="text-xs font-medium text-amber-700 underline">
                              Not set
                            </Link>
                          ) : (
                            <div>
                              <p className="font-medium text-gray-800">{formatMoney(row.monthlyAmount)}</p>
                              <p className="text-xs text-gray-500">{formatMoney(row.perDayAmount)}/day</p>
                            </div>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3">
                          <p className="font-medium text-gray-800">
                            {att.payableDays ?? 0}
                            <span className="font-normal text-gray-500">/{att.days ?? 0} payable</span>
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatMinutes(att.workedMinutes)}
                            {att.openDays > 0 && (
                              <span className="ml-1 font-semibold text-amber-600">· {att.openDays} open</span>
                            )}
                          </p>
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3 text-gray-700">
                          {formatMoney(row.earnedAmount ?? 0)}
                          {(row.bonus > 0 || row.deduction > 0) && (
                            <p className="text-xs">
                              {row.bonus > 0 && <span className="text-emerald-600">+{formatMoney(row.bonus)} </span>}
                              {row.deduction > 0 && <span className="text-red-600">−{formatMoney(row.deduction)}</span>}
                            </p>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3 font-semibold text-gray-800">
                          {formatMoney(row.netPayable ?? 0)}
                        </td>
                        {/* Nothing owed is not a ₹0 amount to read — it's a
                            settled row. Showing "₹0 due" invites a second look
                            at a line that needs none. */}
                        <td className="px-3 sm:px-4 py-2 sm:py-3">
                          {(row.dueAmount ?? 0) > 0 ? (
                            <span className="font-bold text-emerald-700">{formatMoney(row.dueAmount)}</span>
                          ) : row.paidAmount > 0 ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                              <CircleCheck size={13} /> Settled
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3">
                          {/* Without a salary the API still reports PENDING,
                              which reads as "we owe them and haven't paid".
                              Nothing is actually payable, so say that instead
                              of showing a status that invites a click. */}
                          {noSalary ? (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-label-sm font-semibold text-amber-800"
                              title="No salary has been set, so this month can't be priced or paid."
                            >
                              <AlertTriangle size={11} /> No salary
                            </span>
                          ) : (
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-label-sm font-semibold ${
                                PAYROLL_STATUS_STYLES[row.status] ?? "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {PAYROLL_STATUS_LABELS[row.status] ?? row.status}
                            </span>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            {/* Named, not a +/− glyph — this opens the bonus
                                and deduction form, which isn't obvious from
                                two arithmetic signs. */}
                            <button
                              onClick={() => setAdjustRow(row)}
                              title="Add a bonus or a deduction for this month"
                              className="rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"
                            >
                              <SlidersHorizontal size={12} className="mr-1 inline" />
                              Adjust
                            </button>
                            {row.paidAmount > 0 && (
                              <button
                                onClick={() => setReverseRow(row)}
                                title="Reverse a payment"
                                className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                              >
                                <Undo2 size={15} />
                              </button>
                            )}
                            {/* Three genuinely different states, so the button
                                says which one it is in. Paying without a salary
                                is refused by the API anyway (nothing is
                                payable), so it is blocked here with a reason
                                rather than failing after the click. */}
                            <Button
                              size="sm"
                              onClick={() => setPayRow(row)}
                              disabled={noSalary || (row.dueAmount ?? 0) <= 0}
                              title={
                                noSalary
                                  ? "Set this employee's salary before paying them — there is nothing to pay yet."
                                  : (row.dueAmount ?? 0) <= 0
                                    ? "Nothing outstanding for this month."
                                    : `Record a payment of ${formatMoney(row.dueAmount)}`
                              }
                              className={
                                noSalary || (row.dueAmount ?? 0) <= 0
                                  ? "bg-gray-200 text-gray-500 text-xs px-2.5 py-1"
                                  : "bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs px-2.5 py-1"
                              }
                            >
                              {noSalary ? "No salary" : row.dueAmount > 0 ? "Pay" : "Paid"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <p className="mt-4 text-xs text-gray-500">
          Rows are sorted by amount due, so whoever is owed most is at the top. A month nobody has
          touched is computed live from attendance and salary; the first payment or adjustment
          snapshots it, and it freezes once fully paid.
        </p>

        {payRow && (
          <PayModal open onClose={() => setPayRow(null)} row={payRow} month={month} onDone={afterAction} />
        )}
        {adjustRow && (
          <AdjustModal open onClose={() => setAdjustRow(null)} row={adjustRow} month={month} onDone={afterAction} />
        )}
        {reverseRow && (
          <ReverseModal open onClose={() => setReverseRow(null)} row={reverseRow} month={month} onDone={afterAction} />
        )}
      </div>
    </div>
  );
}
